// Ops simulation Sadha T19 — which payments a till may take offline (owner
// decision, Odoo-style):
//   - cash: always;
//   - card / UPI on a manual slip: after the cashier confirms the slip; the
//     bill syncs as 'paid offline — unverified' until a manager checks it;
//   - card on the store's linked terminal, store credit / credit notes, wallet
//     (loyalty) points and Account Credit: refused — each needs the server.

import type { PaymentMethod } from '@bbpos/ui/src/components/POSBillingComponents/PaymentModal/types';

export const ACCOUNT_CREDIT_OFFLINE_MSG =
  'Account Credit needs a connection. Reconnect, or choose another payment method.';

const STORE_CREDIT_OFFLINE_MSG =
  'Store credit needs a connection to check the balance. Take cash, card or UPI, or reconnect.';

const WALLET_OFFLINE_MSG =
  'Wallet and loyalty points need a connection to check the balance. Take cash, card or UPI, or reconnect.';

const TERMINAL_CARD_OFFLINE_MSG =
  "Card payments go through this store's card terminal, which needs a connection. Take cash or UPI, or reconnect.";

const REFUSED: Partial<Record<PaymentMethod, string>> = {
  account_credit: ACCOUNT_CREDIT_OFFLINE_MSG,
  store_credit: STORE_CREDIT_OFFLINE_MSG,
  credit_note: STORE_CREDIT_OFFLINE_MSG,
  wallet: WALLET_OFFLINE_MSG,
};

/**
 * Why this tender cannot be taken offline, or null when it can. A split is
 * refused when any of its legs is.
 */
export function offlineTenderRefusal(
  method: PaymentMethod,
  splits: ReadonlyArray<{ method: PaymentMethod }> | undefined,
  opts: { terminalLinked: boolean },
): string | null {
  const methods = method === 'split' && splits?.length ? splits.map((s) => s.method) : [method];
  for (const m of methods) {
    if (REFUSED[m]) return REFUSED[m]!;
    if (m === 'card' && opts.terminalLinked) return TERMINAL_CARD_OFFLINE_MSG;
  }
  return null;
}

/**
 * The card / UPI part of an offline sale the cashier must confirm a slip for,
 * or null when there is none (cash only).
 */
export function offlineSlipTender(
  method: PaymentMethod,
  splits: ReadonlyArray<{ method: PaymentMethod; amount: number }> | undefined,
  amount: number,
): { methods: Array<'card' | 'upi'>; amount: number } | null {
  if (method === 'card' || method === 'upi') return { methods: [method], amount };
  if (method !== 'split' || !splits?.length) return null;
  const legs = splits.filter((s) => s.method === 'card' || s.method === 'upi');
  if (legs.length === 0) return null;
  const methods = Array.from(new Set(legs.map((s) => s.method as 'card' | 'upi')));
  return { methods, amount: legs.reduce((sum, s) => sum + s.amount, 0) };
}

// ─── Linked card terminal per store ─────────────────────────────────────────
//
// There is no terminal list for the till to read offline, so the last online
// card/UPI verdict is remembered per store: a terminal answered → card is
// terminal-linked (refused offline); none registered → manual slips.

const TERMINAL_KEY_PREFIX = 'bbpos_terminal_linked:';

export function rememberLocationTerminal(locationId: string | null | undefined, linked: boolean): void {
  if (!locationId) return;
  try {
    localStorage.setItem(`${TERMINAL_KEY_PREFIX}${locationId}`, linked ? '1' : '0');
  } catch {
    /* storage unavailable — offline card then falls back to a manual slip */
  }
}

export function isLocationTerminalLinked(locationId: string | null | undefined): boolean {
  if (!locationId) return false;
  try {
    return localStorage.getItem(`${TERMINAL_KEY_PREFIX}${locationId}`) === '1';
  } catch {
    return false;
  }
}
